import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginatedResponseType } from '../Types/Response/PaginatedResponseType';
import axiosAuthorised, { DEV_OVERRIDE_HEADER } from './AxiosAuthorised';
import { MailItemSendsType } from '../Types/Response/Mailboxes/Mailbox';
import { AddressType } from '../Types/AddressType';
import { PaginationPageParameters } from './PaginationHelpers';

export enum SendsPagesUpdateType {
	CREATE,
	DELETE,
	UPDATE,
	STATUS,
	ADD_USER,
	DELETE_USER,
	NEW_ADDRESS,
}

export let allowedPostage: Array<number> = [9, 10, 13, 14, 1023, 1024];

export type SendsAddressType = {
	id: number;
	value?: string;
	postageOptions: Array<number>;
};

export interface SendsPageAddressOptionType extends AddressType {
	formatted: string;
}

export type SendsPostageOptionType = {
	id: number;
	name?: string;
};

type SendsUserType = {
	id: string;
	name?: string;
	emailAddress?: string;
};

export interface SendsPageDataType {
	title: string;
	description: string;
	addresses: Array<SendsAddressType>;
	users: Array<SendsUserType>;
	allowCreateAddress: boolean;
	isActive: boolean;
	defaultEmailAddress?: string;
	defaultPhoneNumber?: string;
	isDeleted?: boolean;

	id?: number;
	dateCreated?: Date;
	liveUrl?: string;
}

export type SendsItemTrackingLinkType = {
	trackingId: string;
	postageName?: string;
};

export type SendsHistoryEntryType = {
	id: number;
	title: string;
	liveUrl: string;
	user: SendsUserType;
	dateCreated: Date;
	isDeleted: boolean;
	mailItem: MailItemSendsType;
	postageOption: SendsPostageOptionType;
	address: SendsPageAddressOptionType;

	hidePageDetails?: boolean;
	hideUserDetails?: boolean;
	linkToSend?: boolean;
};

const createSendsPage = async (sendsData: SendsPageDataType) => {
	const { data } = await axiosAuthorised.post(
		`${process.env.REACT_APP_API_HOST}/client/v1/sends-page`,
		JSON.stringify(sendsData),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const deleteSendsPage = async (pageId: number) => {
	const { data } = await axiosAuthorised.delete(
		`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}`,
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const updateSendsPage = async (pageId: number, sendsData: SendsPageDataType) => {
	const { data } = await axiosAuthorised.patch(
		`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}`,
		JSON.stringify(sendsData),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const updateSendsPageActiveStatus = async (pageId: number, status: boolean) => {
	const { data } = await axiosAuthorised.patch(
		`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}/active`,
		JSON.stringify({ isActive: status }),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const updateSendsPageAddress = async (pageId: number, addressData: any) => {
	const { data } = await axiosAuthorised.patch(
		`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}/addresses`,
		JSON.stringify({ addresses: addressData }),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

export type SendHistoryFilterType = {
	userIds?: Array<string>;
	pageIds?: Array<number>;
	startDate?: string;
	endDate?: string;
};

const useInfiniteAllSendsHistory = (props: SendHistoryFilterType) => {
	let filterSlug = '';

	if (props.userIds) {
		props.userIds.forEach((userId) => (filterSlug += `&userIds=${userId}`));
	}

	if (props.pageIds) {
		props.pageIds.forEach((pageId) => (filterSlug += `&sendsPageIds=${pageId}`));
	}

	if (props.startDate) {
		filterSlug += `&startDate=${props.startDate}`;
	}

	if (props.endDate) {
		filterSlug += `&endDate=${props.endDate}`;
	}

	return useInfiniteQuery(
		['allSendsHistory', props.pageIds, props.userIds, props.endDate, props.startDate],
		async ({
			pageParam = `${process.env.REACT_APP_API_HOST}/client/v1/sends-page/history?offset=0&limit=10${filterSlug}`,
		}) => {
			const { data }: { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam, DEV_OVERRIDE_HEADER);
			return data;
		},
		{
			...PaginationPageParameters,
			staleTime: 600000,
		}
	);
};

const useSendsPage = (pageId: number) => {
	return useQuery(
		['sendsPage', pageId],
		async () => {
			const { data } = await axiosAuthorised.get(
				`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}`,
				DEV_OVERRIDE_HEADER
			);

			return data;
		},
		{
			retry: false,
		}
	);
};

const useSendsPageDetail = (pageId: number) => {
	return useQuery(
		['sendsPageUser', pageId],
		async () => {
			const { data } = await axiosAuthorised.get(
				`${process.env.REACT_APP_API_HOST}/client/v1/sends-page/${pageId}/detail`,
				DEV_OVERRIDE_HEADER
			);

			return data;
		},
		{
			retry: false,
		}
	);
};

const useInfiniteSendsPage = () => {
	return useInfiniteQuery(
		['allSends'],
		async ({ pageParam = `${process.env.REACT_APP_API_HOST}/client/v1/sends-page/list?offset=0&limit=10` }) => {
			const { data }: { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam, DEV_OVERRIDE_HEADER);
			return data;
		},
		{
			...PaginationPageParameters,
		}
	);
};

const useInfiniteShortListSendsPage = () => {
	return useInfiniteQuery(
		['allSendsShort'],
		async ({
			pageParam = `${process.env.REACT_APP_API_HOST}/client/v1/sends-page/listshortitem?offset=0&limit=20`,
		}) => {
			const { data }: { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam, DEV_OVERRIDE_HEADER);
			return data;
		},
		{
			...PaginationPageParameters,
		}
	);
};

export {
	createSendsPage,
	deleteSendsPage,
	updateSendsPage,
	updateSendsPageAddress,
	updateSendsPageActiveStatus,
	useSendsPage,
	useSendsPageDetail,
	useInfiniteSendsPage,
	useInfiniteShortListSendsPage,
	useInfiniteAllSendsHistory,
};

const useChangeSendsStatus = () => {
	const queryClient = useQueryClient();
	let QUERY_KEY = ['allSends'];
	let QUERY_PAGE_KEY = ['sendsPage'];

	return useMutation(async () => null, {
		onMutate: async ({ id, newData }: { id: any; newData: any }) => {
			QUERY_PAGE_KEY = ['sendsPage', id];

			await queryClient.cancelQueries(QUERY_KEY);

			const previousData: any = queryClient.getQueryData(QUERY_KEY);

			let modifiedData: any = queryClient.getQueryData(QUERY_KEY);
			let modifiedPageData: any = queryClient.getQueryData(QUERY_PAGE_KEY);

			if (modifiedData) {
				modifiedData.pages.map((group: any, index: number) =>
					group.data.forEach((entry: SendsPageDataType) => {
						if (entry.id === id) {
							entry.isActive = newData;
						}
					})
				);

				queryClient.setQueryData(QUERY_KEY, modifiedData);
			}

			if (modifiedPageData) {
				modifiedPageData.isActive = newData;
				queryClient.setQueryData(QUERY_PAGE_KEY, modifiedPageData);
			}

			return { previousData, modifiedData };
		},
		onError: (err, newData: any, context) => {
			queryClient.setQueryData(QUERY_KEY, context?.previousData);
		},
	});
};

const useUpdateSendsData = () => {
	const queryClient = useQueryClient();
	let QUERY_KEY = ['allSends'];
	let QUERY_PAGE_KEY = ['sendsPage'];

	return useMutation(async () => null, {
		onMutate: async ({ newData, updateType }: { newData: any; updateType: SendsPagesUpdateType }) => {
			QUERY_PAGE_KEY = ['sendsPage', newData.id];

			await queryClient.cancelQueries(QUERY_KEY);
			await queryClient.cancelQueries(QUERY_PAGE_KEY);

			const previousData: any = queryClient.getQueryData(['sendsPage', newData.id]);

			let modifiedData: any = queryClient.getQueryData(QUERY_KEY);

			if (modifiedData) {
				switch (updateType) {
					case SendsPagesUpdateType.CREATE:
						modifiedData.pages.map((group: any, index: number) => {
							modifiedData.pages[index].data.push(newData);
						});
						break;
					case SendsPagesUpdateType.DELETE:
						modifiedData.pages.map((group: any, index: number) => {
							modifiedData.pages[index].data = group.data.filter((entry: SendsPageDataType) => {
								return !entry.id !== newData.id;
							});
						});
						break;
					case SendsPagesUpdateType.UPDATE:
						for (let i = 0; i < modifiedData.length; i++) {
							if (modifiedData[i].id === newData.id) {
								modifiedData[i] = newData;
							}
						}
						break;
					case SendsPagesUpdateType.NEW_ADDRESS:
						for (let i = 0; i < modifiedData.length; i++) {
							if (modifiedData[i].id === newData.id) {
								modifiedData[i].addresses = newData.addresses;
							}
						}
						break;
				}
			}

			queryClient.setQueryData(QUERY_KEY, modifiedData);
			queryClient.setQueryData(QUERY_PAGE_KEY, newData);

			return { previousData, modifiedData };
		},
		onError: (err, newData: any, context) => {
			queryClient.setQueryData(QUERY_KEY, context?.previousData);
		},
		onSettled: () => {
			queryClient.invalidateQueries(QUERY_KEY).then(() => null);
		},
	});
};

const useUpdateSendsDetail = () => {
	const queryClient = useQueryClient();
	let QUERY_KEY = ['sendsPageUser'];

	return useMutation(async () => null, {
		onMutate: async ({ newData }: { newData: any }) => {
			QUERY_KEY = ['sendsPageUser', newData.id];

			await queryClient.cancelQueries(QUERY_KEY);

			const previousData: any = queryClient.getQueryData(['sendsPageUser', newData.id]);

			let modifiedData: any = queryClient.getQueryData(QUERY_KEY);

			if (modifiedData) {
				modifiedData.addresses = newData.addresses;
			}

			queryClient.setQueryData(QUERY_KEY, modifiedData);

			return { previousData, modifiedData };
		},
		onError: (err, newData: any, context) => {
			queryClient.setQueryData(QUERY_KEY, context?.previousData);
		},
		onSettled: () => {
			queryClient.invalidateQueries(QUERY_KEY).then(() => null);
		},
	});
};

export { useChangeSendsStatus, useUpdateSendsData, useUpdateSendsDetail };
